import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

type TransferStatus = "pending" | "accepted" | "cancelled" | "expired";

const statusColors: Record<TransferStatus, string> = {
  pending: "var(--warning)",
  accepted: "var(--success)",
  cancelled: "var(--ink-mute)",
  expired: "var(--danger)",
};

interface TransferItem {
  id: string;
  dppId: string;
  brand: string;
  model: string;
  serialNumber: string;
  fromEmail: string | null;
  toEmail: string;
  status: TransferStatus;
  note: string | null;
  initiatedByAdminId: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

interface TransferList {
  items: TransferItem[];
  total: number;
}

const STATUS_FILTERS = [
  "All status",
  "pending",
  "accepted",
  "cancelled",
  "expired",
] as const;
const LIMIT = 20;

function fmtDate(iso: string) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${mo}.${day} · ${h}:${min}`;
}

function StatusPill({ status }: { status: TransferStatus }) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 9.5,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        padding: "3px 8px",
        border: `1px solid ${statusColors[status]}`,
        color: statusColors[status],
      }}
    >
      {status}
    </span>
  );
}

function Party({ label, email }: { label: string; email: string | null }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <span
        className="mono-cap text-mute"
        style={{ fontSize: 10, letterSpacing: "0.12em" }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: "var(--ink)",
          fontFamily: "var(--f-mono)",
          wordBreak: "break-word",
        }}
      >
        {email || "—"}
      </span>
    </div>
  );
}

export function TransfersPage() {
  const { can } = useAuth();
  const canManage = can("dpp:transfer");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All status");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<TransferList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<TransferItem | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      page: String(page),
      limit: String(LIMIT),
    });
    if (statusFilter !== "All status") params.set("status", statusFilter);
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

    api
      .get<TransferList>(`/admin/transfers?${params.toString()}`)
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled)
          setError(apiErrorMessage(err, "Failed to load transfers"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => load(), [load, reloadFlag]);

  async function handleCancel() {
    if (!selected) return;
    setCancelling(true);
    setError("");
    try {
      await api.post(`/admin/transfers/${selected.id}/cancel`, {});
      setSelected(null);
      setConfirmingCancel(false);
      setReloadFlag((n) => n + 1);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to cancel transfer"));
    } finally {
      setCancelling(false);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  return (
    <>
      <div className="adm__body">
        <div className="adm__page-head" style={{ marginBottom: 16 }}>
          <h1
            style={{
              fontFamily: "var(--f-head)",
              fontWeight: 500,
              fontSize: 26,
              margin: 0,
            }}
          >
            DPP transfers
          </h1>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, maxWidth: 460 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--surface-2)",
                border: "1px solid var(--line)",
                padding: "8px 14px",
              }}
            >
              <Input
                type="search"
                placeholder="Search by email, brand, model or serial…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  background: "transparent",
                  border: 0,
                  outline: 0,
                  color: "var(--ink)",
                  fontFamily: "var(--f-body)",
                  fontSize: 13,
                  width: "100%",
                  padding: 0,
                }}
              />
            </div>
          </div>
          <select
            className="select"
            style={{
              width: "auto",
              padding: "9px 32px 9px 14px",
              fontSize: 12,
              appearance: "none",
            }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTERS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setStatusFilter("All status");
            }}
          >
            Clear
          </Button>
        </div>

        {error && (
          <div
            style={{
              padding: "12px 16px",
              border: "1px solid var(--danger)",
              color: "var(--danger)",
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Watch</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    padding: "32px 0",
                  }}
                >
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && (data?.items.length ?? 0) === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    padding: "32px 0",
                  }}
                >
                  No transfers
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              data?.items.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => setSelected(row)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>
                    {[row.brand, row.model].filter(Boolean).join(" · ")}
                    <span
                      className="mono"
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "var(--ink-mute)",
                      }}
                    >
                      {row.serialNumber}
                    </span>
                  </TableCell>
                  <TableCell className="mono">{row.fromEmail || "—"}</TableCell>
                  <TableCell className="mono">{row.toEmail}</TableCell>
                  <TableCell className="mono">
                    {fmtDate(row.createdAt)}
                  </TableCell>
                  <TableCell>
                    <StatusPill status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {data && data.total > LIMIT && (
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span
              className="mono-cap text-mute"
              style={{ fontSize: 11, letterSpacing: "0.12em" }}
            >
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={!!selected}
        title="Transfer detail"
        onClose={() => {
          setSelected(null);
          setConfirmingCancel(false);
        }}
      >
        {selected && (
          <div style={{ display: "grid", gap: 18 }}>
            <div style={{ display: "grid", gap: 4 }}>
              <span
                className="mono-cap text-mute"
                style={{ fontSize: 10, letterSpacing: "0.12em" }}
              >
                Watch
              </span>
              <Link
                to="/dpp"
                search={{
                  brand: selected.brand,
                  model: selected.model,
                  serial: selected.serialNumber,
                }}
                style={{ fontSize: 14, color: "var(--accent)" }}
              >
                {[selected.brand, selected.model].filter(Boolean).join(" · ")} ·{" "}
                {selected.serialNumber}
              </Link>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <Party label="Sender" email={selected.fromEmail} />
              <Party label="Receiver" email={selected.toEmail} />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                alignItems: "baseline",
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Status
                </span>
                <StatusPill status={selected.status} />
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  {selected.status === "accepted" ? "Accepted" : "Expires"}
                </span>
                <span style={{ fontSize: 13, fontFamily: "var(--f-mono)" }}>
                  {fmtDate(
                    selected.status === "accepted" && selected.acceptedAt
                      ? selected.acceptedAt
                      : selected.expiresAt
                  )}
                </span>
              </div>
            </div>

            {selected.note && (
              <div style={{ display: "grid", gap: 4 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Note
                </span>
                <span style={{ fontSize: 13, color: "var(--ink)" }}>
                  {selected.note}
                </span>
              </div>
            )}

            {canManage && selected.status === "pending" && (
              <div
                style={{ borderTop: "1px solid var(--line)", paddingTop: 16 }}
              >
                {confirmingCancel ? (
                  <div style={{ display: "grid", gap: 12 }}>
                    <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                      Cancel this transfer? The claim link sent to{" "}
                      <strong>{selected.toEmail}</strong> will stop working. This
                      can't be undone.
                    </span>
                    <div className="row gap-12">
                      <Button
                        variant="destructive"
                        onClick={handleCancel}
                        disabled={cancelling}
                      >
                        {cancelling ? "Cancelling…" : "Yes, cancel transfer"}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmingCancel(false)}
                        disabled={cancelling}
                      >
                        Keep it
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmingCancel(true)}
                  >
                    Cancel transfer
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
